import React, { useCallback, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { api, getApiErrorMessage } from '../../../api/client';
import { useSyncStore } from '../../../lib/offline/sync-store';
import { formatInstant } from '../../../lib/calendar-date';
import { unwrapArray } from '../../../lib/api-envelope';
import { LOAD_FAILURE_QUERY_OPTIONS, queryFailureError, queryLoadFailure } from '../../../lib/load-failure';
import { unwrap } from '../../../lib/api-envelope';
import DispatchOutcomeList, { DispatchLoadNote } from '../../../components/rentals/DispatchOutcomeList';
import type { RentalDispatchCompletion } from '../../../types/rental-dispatch-complete';
import { LoadFailureNotice } from '../../../components/ui/LoadFailureNotice';
import type { RentalOrderAssetRow, RentalPeriodListRow } from '../../../types/rental-period';
import { useTenantTimeZone } from '../../../hooks/useTenantTimeZone';
import SignaturePad from '../../../components/SignaturePad';
import { HaulHoursField, haulHoursBody } from '../../../components/rentals/HaulHoursField';
import { useBillHaulHourly } from '../../../hooks/useBillHaulHourly';
import OrphanedOfflineWork from '../../../components/rentals/OrphanedOfflineWork';
import RentalAssetChecklist from '../../../components/rentals/RentalAssetChecklist';
import { RentalInspectionPhotoField, newInspectionId } from '../../../components/rentals/RentalInspectionPhotos';
import {
  inspectionPhotoExtras, rentalChecklistComplete, rentalOverall, structuredInspectionBody, type RentalAnswers,
} from '../../../lib/rental-inspections';
import type { RentalChecklistResponse } from '../../../types/rental-inspection';
import {
  ArrowLeft,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  RefreshCw,
  WifiOff,
  Package,
} from 'lucide-react';

/**
 * `dispatch.assets` is whatever the Dispatch Board saved: a list of LABELS
 * (asset names, or free text typed into the board), not asset ids. It names
 * what the dispatcher meant to send; it cannot identify a row to write. So it
 * is shown for reference only, and the assets this screen writes against come
 * from the order (see `deliverable` below).
 */
function dispatchLabels(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((a) => (typeof a === 'string' ? a : (a as { name?: unknown } | null)?.name))
    .filter((a): a is string => typeof a === 'string' && a.trim().length > 0);
}

export default function DeliveryConfirmation() {
  const navigate = useNavigate();
  const { dispatchId } = useParams<{ dispatchId: string }>();
  const { isOnline } = useSyncStore();
  // The on-rent stamp and the billing start day are the TENANT's
  // (companies.time_zone), never the phone's.
  const timeZone = useTenantTimeZone();

  // Form state
  const [delivered, setDelivered] = useState<Record<string, boolean>>({});
  /**
   * Each delivered asset's pre-delivery checklist (api#861): base list plus its
   * category's family, answered Pass / Minor / Fail / N/A. The server derives
   * the overall result and the asset condition; there is no condition picker.
   */
  const [answers, setAnswers] = useState<Record<string, RentalAnswers>>({});
  const [checklists, setChecklists] = useState<Record<string, RentalChecklistResponse>>({});
  const onChecklistFor = useCallback(
    (assetId: string) => (cl: RentalChecklistResponse) =>
      setChecklists((prev) => (prev[assetId] === cl ? prev : { ...prev, [assetId]: cl })),
    [],
  );
  /**
   * Each asset's inspection id, minted once on this screen so its photos can be
   * uploaded against it before the inspection exists (api#1066). Kept for the
   * life of the screen: a retry resends the same ids, and the API replays a
   * delivery that already landed before it looks at them.
   */
  const inspectionIds = useRef<Record<string, string>>({});
  const inspectionIdFor = (assetId: string) => (inspectionIds.current[assetId] ??= newInspectionId());
  const [photos, setPhotos] = useState<Record<string, string[]>>({});
  const [photosBusy, setPhotosBusy] = useState<Record<string, boolean>>({});
  const [operatorName, setOperatorName] = useState('');
  /**
   * The arrival instant, fixed at the first press. A retry resends the SAME
   * request, so if the first one landed and only its answer was lost, the API
   * replays the stored result instead of writing a second delivery (api#945).
   */
  const arrivedAt = useRef<string | null>(null);
  const [signature, setSignature] = useState<string | null>(null);
  const [haulHours, setHaulHours] = useState('');
  const [submitted, setSubmitted] = useState(false);

  // Fetch dispatch details
  const {
    data: dispatch,
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: ['rental-dispatch', dispatchId],
    queryFn: () =>
      api.rentals.dispatch
        .get(dispatchId!)
        .then((r: any) => r.data?.data ?? r.data ?? null),
    enabled: !!dispatchId,
    retry: 1,
  });

  // GET /dispatches/:id sends `rd.*`: `order_id`, never `rental_order_id`.
  const orderId: string = dispatch?.order_id ?? '';
  const orderNumber: string = dispatch?.order_number ?? '—';
  const listedOnDispatch = dispatchLabels(dispatch?.assets);
  // Haul hours (api#1054): only when the company bills hauls by the hour and
  // this dispatch names a truck — no truck, no haul line.
  const { on: billHaulHourly } = useBillHaulHourly();
  const askHaulHours = billHaulHourly && !!dispatch?.truck_id;

  /**
   * WHICH ASSETS CAN BE DELIVERED: the order's assets with no open rental
   * period on this order. An open period means it is already on rent here,
   * and a second period would bill it twice.
   */
  const orderAssetsQuery = useQuery({
    queryKey: ['rental-order-assets', orderId],
    queryFn: () => api.rentals.orders.getAssets(orderId).then((r) => unwrapArray<RentalOrderAssetRow>(r.data)),
    enabled: !!orderId,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
  const periodsQuery = useQuery({
    queryKey: ['rental-order-periods', orderId],
    queryFn: () => api.rentals.orders.listPeriods(orderId).then((r) => unwrapArray<RentalPeriodListRow>(r.data)),
    enabled: !!orderId,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
  const assetsFailedQuery = [orderAssetsQuery, periodsQuery].find((q) => queryLoadFailure(q) != null);
  const assetsLoading = !assetsFailedQuery && (orderAssetsQuery.isLoading || periodsQuery.isLoading);
  // Open means the API said null; `rp.*` always carries the column.
  const onRentHere = new Set((periodsQuery.data ?? []).filter((p) => p.off_rent_at === null).map((p) => p.asset_id));
  const deliverable = (orderAssetsQuery.data ?? []).filter((a) => !onRentHere.has(a.asset_id));
  const chosen = deliverable.filter((a) => delivered[a.asset_id]);
  /**
   * A unit that FAILS its pre-delivery inspection is not delivered: the
   * inspection puts it in maintenance with a work order (api#861), and it gets
   * no rental period — billing a unit that failed at the gate is the bug
   * api#863 also refuses server-side (409).
   */
  const failedIds = new Set(chosen.filter((a) => rentalOverall(answers[a.asset_id]) === 'fail').map((a) => a.asset_id));
  const allAnswered = chosen.every(
    (a) => rentalChecklistComplete(checklists[a.asset_id], answers[a.asset_id]) && rentalOverall(answers[a.asset_id]) !== null,
  );
  const arrivalDisplay =
    formatInstant(arrivedAt.current ?? new Date().toISOString(), timeZone, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    }, 'en-US') ?? '—';

  const toggleDelivered = (assetId: string) => {
    setDelivered((prev) => ({ ...prev, [assetId]: !prev[assetId] }));
  };

  /**
   * ONE REQUEST: POST /dispatches/:id/deliver (api#945). Per chosen asset the
   * STRUCTURED pre-delivery inspection (its checklist answered item by item,
   * the rep's name and signature); the API then opens the rental period — or,
   * on a Fail, opens a work order and puts the asset in maintenance — and marks
   * the dispatch completed, all in one transaction. Nothing is half-written.
   *
   * It used to be inspection, dispatch status and period as separate requests,
   * reporting "Partly saved" when one failed part-way.
   *
   * No rate on file is NOT a refusal: the asset goes on rent and the office
   * sees `rate_missing` on the order and billing screens. The tech is not told.
   */
  const submitMutation = useMutation({
    // Never paused: the default `online` mode parks a mutation while the
    // device looks offline, and the hand sees a spinner instead of the failure.
    networkMode: 'always',
    mutationFn: async (): Promise<RentalDispatchCompletion> => {
      arrivedAt.current ??= new Date().toISOString();
      const at = arrivedAt.current;
      const res = await api.rentals.dispatch.deliver(dispatchId!, {
        delivered_at: at,
        ...(askHaulHours ? haulHoursBody(haulHours) : {}),
        assets: chosen.map((a) =>
          structuredInspectionBody(a.asset_id, 'pre_delivery', checklists[a.asset_id], answers[a.asset_id], {
            signed_by_name: operatorName.trim(),
            signature_url: signature,
            signed_at: at,
            ...inspectionPhotoExtras(inspectionIdFor(a.asset_id), photos[a.asset_id] ?? []),
          }),
        ),
      });
      const data = unwrap<RentalDispatchCompletion>(res.data);
      if (!data) throw new Error('The delivery was sent, but the answer could not be read. Check with the office before trying again.');
      return data;
    },
    onSuccess: () => {
      setSubmitted(true);
    },
  });

  /**
   * ALWAYS ATTEMPT THE REQUEST, even when `isOnline` says otherwise.
   *
   * This used to short-circuit on `!isOnline` and write the payload to a
   * localStorage array nothing ever read, then show "Saved Offline — will
   * sync automatically when connected". The work was dropped and the hand was
   * told it was safe.
   *
   * There is no queue to fall back to, so the honest thing is to try and let
   * the failure show. Trusting `isOnline` to refuse pre-emptively would also
   * be wrong in the other direction: that flag is a heuristic over a health
   * poll that has reported false before, and refusing a save that would have
   * succeeded is its own way of losing work.
   */
  const handleSubmit = () => {
    submitMutation.mutate();
  };

  // A photo still uploading is not confirmed, and the API refuses the lot for it.
  const photosUploading = chosen.some((a) => photosBusy[a.asset_id]);
  const canSubmit = !!signature && operatorName.trim().length > 0 && !!orderId && chosen.length > 0 && allAnswered && !photosUploading;
  const submitError = submitMutation.error;

  // Full-screen loading
  if (isLoading) {
    return (
      <div className="min-h-full bg-fieldiq-dark flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-fieldiq-accent animate-spin" />
      </div>
    );
  }

  // Full-screen error
  if (isError) {
    return (
      <div className="min-h-full bg-fieldiq-dark flex flex-col items-center justify-center px-6">
        <AlertTriangle className="w-12 h-12 text-amber-400 mb-4" />
        <h2 className="text-lg font-bold text-white mb-2">Unable to Load Dispatch</h2>
        <p className="text-sm text-fieldiq-muted text-center mb-6">
          Could not load dispatch details. Check your connection and try again.
        </p>
        <button
          onClick={() => refetch()}
          className="flex items-center gap-2 px-6 py-3 bg-fieldiq-accent text-fieldiq-dark font-semibold rounded-xl min-h-[48px]"
        >
          <RefreshCw size={18} /> Retry
        </button>
      </div>
    );
  }

  // Success screen
  if (submitted) {
    return (
      <div className="min-h-full bg-fieldiq-dark flex flex-col items-center justify-center px-6">
        <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mb-4">
          <CheckCircle2 className="w-10 h-10 text-green-400" />
        </div>
        <h2 className="text-xl font-bold text-white mb-2">Delivery Confirmed</h2>
        <p className="text-sm text-fieldiq-muted text-center mb-1">
          Order #{orderNumber}
        </p>
        <p className="text-xs text-fieldiq-muted text-center mb-4">Arrived {arrivalDisplay}.</p>
        <DispatchOutcomeList assets={submitMutation.data?.assets ?? []} />
        <DispatchLoadNote load={submitMutation.data?.load} />
        <button
          onClick={() => navigate('/field/rentals')}
          className="px-8 py-3 bg-fieldiq-accent text-fieldiq-dark font-semibold rounded-xl min-h-[48px]"
        >
          Back to Dashboard
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-fieldiq-dark pb-8">
      <OrphanedOfflineWork />

      {/* Offline banner */}
      {!isOnline && (
        <div className="bg-amber-500/20 border-b border-amber-500/30 px-4 py-2 text-center">
          <div className="flex items-center justify-center gap-2">
            <WifiOff size={14} className="text-amber-400" />
            <span className="text-xs font-medium text-amber-400">
              Offline — you'll need signal to confirm delivery
            </span>
          </div>
        </div>
      )}

      {/* Header */}
      <div
        className="bg-fieldiq-dark border-b border-fieldiq-border px-4 py-3"
        style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 0.75rem)' }}
      >
        <div className="max-w-[430px] mx-auto flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="w-10 h-10 rounded-lg bg-fieldiq-card flex items-center justify-center min-h-[48px] min-w-[48px]"
          >
            <ArrowLeft size={20} className="text-white" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-base font-bold text-white">Delivery Confirmation</h1>
            {/* The order the tech is delivering, not the first eight characters of a
              * dispatch UUID (fix/field-home-trucking-rentals). */}
            <p className="text-xs text-fieldiq-muted">Order #{orderNumber}</p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-[430px] mx-auto px-4 py-4 space-y-4">
        {/* Wellsite Info */}
        <div className="bg-fieldiq-card border border-fieldiq-border rounded-xl p-4">
          <h3 className="text-xs font-bold text-fieldiq-muted uppercase tracking-wider mb-3">
            Wellsite Info
          </h3>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-xs text-fieldiq-muted">Customer</span>
              <span className="text-sm text-white font-medium">
                {dispatch?.customer_name || dispatch?.customer || '—'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-xs text-fieldiq-muted">Well / Site</span>
              <span className="text-sm text-white font-medium">
                {dispatch?.well_name || dispatch?.site_name || '—'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-xs text-fieldiq-muted">LSD</span>
              <span className="text-sm text-white font-medium">
                {dispatch?.lsd || dispatch?.legal_description || '—'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-xs text-fieldiq-muted">Contact</span>
              <span className="text-sm text-white font-medium">
                {dispatch?.contact_name || dispatch?.site_contact || '—'}
              </span>
            </div>
          </div>
        </div>

        {/* Equipment Section */}
        <div className="bg-fieldiq-card border border-fieldiq-border rounded-xl p-4">
          <h3 className="text-xs font-bold text-fieldiq-muted uppercase tracking-wider mb-1">
            Equipment
          </h3>
          <p className="text-xs text-fieldiq-muted mb-3">
            {listedOnDispatch.length > 0
              ? `Dispatch lists: ${listedOnDispatch.join(', ')}. Tick each unit that arrived.`
              : 'Tick each unit that arrived.'}
          </p>
          {assetsFailedQuery ? (
            <LoadFailureNotice error={queryFailureError(assetsFailedQuery)} subject="assets on this order" />
          ) : assetsLoading ? (
            <div className="flex justify-center py-4">
              <Loader2 className="w-5 h-5 text-fieldiq-muted animate-spin" />
            </div>
          ) : !orderId ? (
            <p className="text-sm text-fieldiq-muted py-2">This dispatch names no order, so there is nothing to deliver against.</p>
          ) : deliverable.length === 0 ? (
            <div className="flex flex-col items-center py-4 text-fieldiq-muted">
              <Package size={24} className="mb-2 opacity-40" />
              <p className="text-sm">
                {(orderAssetsQuery.data ?? []).length === 0
                  ? 'No assets on this order'
                  : 'Every asset on this order is already on rent'}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {deliverable.map((asset) => {
                const assetId = asset.asset_id;
                const isDelivered = !!delivered[assetId];
                return (
                  <div
                    key={assetId}
                    data-testid="delivery-asset"
                    className="bg-fieldiq-dark border border-fieldiq-border rounded-lg p-3"
                  >
                    <label className="flex items-center gap-3 min-h-[40px] mb-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={isDelivered}
                        onChange={() => toggleDelivered(assetId)}
                        aria-label={`${asset.asset_number} arrived`}
                        className="w-5 h-5 accent-green-500"
                      />
                      <span className="min-w-0">
                        <span className="block text-sm font-semibold text-white">{asset.asset_name}</span>
                        <span className="block text-xs text-fieldiq-muted">{asset.asset_number}</span>
                      </span>
                    </label>
                    {isDelivered && (
                      <>
                        <RentalAssetChecklist
                          compact
                          assetId={assetId}
                          inspectionType="pre_delivery"
                          answers={answers[assetId] ?? {}}
                          onChange={(next) => setAnswers((prev) => ({ ...prev, [assetId]: next }))}
                          onChecklist={onChecklistFor(assetId)}
                        />
                        {failedIds.has(assetId) && (
                          <p role="note" data-testid="delivery-fail-note" className="mt-2 text-xs text-fieldiq-text">
                            Failed: {asset.asset_number} goes to maintenance with a work order and is not put on rent.
                          </p>
                        )}
                        <div className="mt-3">
                          <RentalInspectionPhotoField
                            inspectionId={inspectionIdFor(assetId)}
                            idPrefix={`delivery-${assetId}`}
                            label={`${asset.asset_number} photos`}
                            onChange={(keys) => setPhotos((prev) => ({ ...prev, [assetId]: keys }))}
                            onBusyChange={(busy) => setPhotosBusy((prev) => ({ ...prev, [assetId]: busy }))}
                          />
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Arrival time */}
        <div className="bg-fieldiq-card border border-fieldiq-border rounded-xl p-4">
          <h3 className="text-xs font-bold text-fieldiq-muted uppercase tracking-wider mb-2">
            Arrival
          </h3>
          <span className="text-sm text-white font-medium">{arrivalDisplay}</span>
          <p className="text-xs text-fieldiq-muted mt-1">
            Recorded as the on-rent time when you confirm. Billing then follows the order's billing trigger.
          </p>
        </div>

        {askHaulHours && <HaulHoursField value={haulHours} onChange={setHaulHours} />}

        {/* Operator Signature */}
        <div className="space-y-3">
          <div className="bg-fieldiq-card border border-fieldiq-border rounded-xl p-4">
            <h3 className="text-xs font-bold text-fieldiq-muted uppercase tracking-wider mb-3">
              Operator Representative
            </h3>
            <input
              type="text"
              value={operatorName}
              onChange={(e) => setOperatorName(e.target.value)}
              placeholder="Operator rep name"
              className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-3 text-sm text-white placeholder-fieldiq-muted min-h-[48px]"
            />
          </div>
          <SignaturePad
            label="Operator Signature"
            onSave={(dataUrl) => setSignature(dataUrl)}
            onClear={() => setSignature(null)}
            value={signature || undefined}
          />
        </div>

        {/* Submit */}
        <button
          onClick={handleSubmit}
          disabled={!canSubmit || submitMutation.isPending}
          className={`w-full py-4 rounded-xl font-semibold text-base min-h-[56px] transition-colors ${
            canSubmit && !submitMutation.isPending
              ? 'bg-fieldiq-accent text-fieldiq-dark active:bg-fieldiq-accent/90'
              : 'bg-fieldiq-border text-fieldiq-muted cursor-not-allowed'
          }`}
        >
          {submitMutation.isPending ? (
            <Loader2 className="w-5 h-5 animate-spin mx-auto" />
          ) : (
            'Confirm Delivery'
          )}
        </button>

        {submitMutation.isError && (
          <div role="alert" className="bg-red-500/10 border border-red-500/30 rounded-lg p-3">
            <p className="text-sm font-semibold text-red-400 mb-1">Not saved</p>
            <p className="text-xs text-red-300/90">
              {getApiErrorMessage(submitError, submitError instanceof Error ? submitError.message : 'The delivery could not be saved.')}
            </p>
            <p className="text-xs text-red-300/90 mt-1">
              {isOnline
                  ? 'Try again, and tell the office if it keeps failing.'
                  : "There's no connection, and this screen can't hold the confirmation for later. Keep this page open and press Confirm Delivery again once you have signal."}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
